import { useState, useRef, useCallback } from "react";
import { getAuthToken } from "@Security/stateHelpers";
import { APP_WS_ENDPOINT } from "@API/index";

const CHUNK_SIZE = 64 * 1024;
const BATCH_LIMIT = 50 * 1024 * 1024; // 50 MB

export const useWebSocketDocumentUploader = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [results, setResults] = useState(null);

  const wsRef = useRef(null);
  const uploadedFilesRef = useRef([]);
  const errorRef = useRef("");

  const setErrorWithRef = useCallback((errorMessage) => {
    errorRef.current = errorMessage;
    setError(errorMessage);
  }, []);

  const connect = useCallback(
    (arg1, requestIdsArg = [], requireInnInOSVArg = false) => {
      const opts =
        arg1 && typeof arg1 === "object"
          ? arg1
          : { clientId: arg1, requestIds: requestIdsArg, requireInnInOSV: requireInnInOSVArg };

      const { clientId, leadId, requestIds = [], requireInnInOSV = false } = opts;

      return new Promise((resolve, reject) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          resolve();
          return;
        }

        setIsConnecting(true);
        setErrorWithRef("");
        setSuccess(false);
        setProgress(0);
        setStatus("Инициализация...");
        setResults(null);

        const idParam = leadId ? `leadId=${leadId}` : `clientId=${clientId}`;
        let wsUrl = `${APP_WS_ENDPOINT()}/api/findatarequest/ws/upload-documents?${idParam}&requireInnInOSV=${requireInnInOSV}`;
        console.log("WebSocket URL:", wsUrl);
        if (requestIds && requestIds.length > 0) {
          requestIds.forEach((id) => {
            wsUrl += `&requestId=${id}`;
          });
        }

        try {
          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;

          ws.onopen = () => {
            setIsConnecting(false);
            setIsConnected(true);
            // setStatus("Подключено. Авторизация...");

            const authMessage = {
              type: "auth",
              accessToken: getAuthToken(),
            };

            ws.send(JSON.stringify(authMessage));
            resolve();
          };

          ws.onmessage = (event) => {
            try {
              if (event.data === "ping" || event.data === "pong") {
                return;
              }

              const response = JSON.parse(event.data);
              console.log("WebSocket message received:", response);

              if (response.type === "progress") {
                setProgress(response.progress || 0);
                setStatus(response.message || "");
              } else if (
                response.success === true &&
                response.data &&
                typeof response.data === "string" &&
                response.data.includes("All batch files loaded successfully")
              ) {
                setStatus("Файлы отправлены. Начало обработки...");
                setTimeout(() => {
                  const processMessage = {
                    type: "process",
                  };
                  ws.send(JSON.stringify(processMessage));
                }, 500);
              } else if (
                response.success === true &&
                response.message === "Operation completed successfully"
              ) {
                setSuccess(true);
                setProgress(100);
                setStatus(response.data?.message);
                setResults(response.data);

                setTimeout(() => {
                  if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.isManualDisconnect = true;
                    ws.close(1000, "Upload completed successfully");
                  }
                }, 500);
              } else if (response.success === false || response.errors) {
                const errorMessage =
                  response.message ||
                  response.errors?.[0] ||
                  "Ошибка выполнения";
                setErrorWithRef(errorMessage);
                setStatus(errorMessage);
                setProgress(100);

                if (response.data) {
                  setResults(response.data);
                }

                setTimeout(() => {
                  if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.close(1000, "Server error");
                  }
                }, 1000);
              } else if (response.message || response.data) {
                setStatus(response.message || response.data || "");
                if (response.progress !== undefined) {
                  setProgress(response.progress || 0);
                }
              }
            } catch (e) {
              if (
                event.data === "ping" ||
                event.data === "pong" ||
                event.data.length < 10
              ) {
                return;
              }
              console.warn("Failed to parse message from server:", event.data);
            }
          };

          ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            setErrorWithRef("Ошибка соединения");
            setIsConnecting(false);
            setIsConnected(false);
            reject(error);
          };

          ws.onclose = (event) => {
            setIsConnecting(false);
            setIsConnected(false);

            if (
              !ws.isManualDisconnect &&
              event.code !== 1000 &&
              !errorRef.current
            ) {
              console.error("WebSocket closed with error:", event);
              setErrorWithRef(
                `Соединение разорвано: ${event.reason || "Неизвестная ошибка"}`
              );
            }

            reject(new Error(`WebSocket connection closed: ${event.code}`));
          };
        } catch (error) {
          setIsConnecting(false);
          setErrorWithRef("Websocket connection error");
          reject(error);
        }
      });
    },
    [success]
  );

  const uploadFiles = useCallback(async (files) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error("WebSocket не подключен");
    }

    if (!files || files.length === 0) {
      throw new Error("Нет файлов для загрузки");
    }

    uploadedFilesRef.current = [];
    setProgress(0);
    setErrorWithRef("");
    setSuccess(false);
    setResults(null);

    try {
      // метод загрузки: batch или chunked
      const totalSize = Array.from(files).reduce(
        (sum, file) => sum + file.size,
        0
      );

      const estimatedEncodedSize =
        Math.ceil((totalSize * 4) / 3) + files.length * 200;

      if (estimatedEncodedSize <= BATCH_LIMIT) {
        await uploadFilesBatch(files);
      } else {
        await uploadFilesChunked(files);
      }
    } catch (error) {
      setErrorWithRef(error.message);
      throw error;
    }
  }, []);

  const uploadFilesBatch = useCallback(async (files) => {
    // setStatus(`Начинаем batch загрузку ${files.length} файлов`);

    const batchFiles = [];
    let processedFiles = 0;

    for (const file of files) {
      const base64Data = await fileToBase64(file);
      batchFiles.push({
        fileName: file.name,
        contentType: file.type,
        data: base64Data.split(",")[1],
        fileSize: file.size,
      });

      processedFiles++;
    }

    const batchMessage = {
      type: "batch_files",
      batchFiles: batchFiles,
    };

    wsRef.current.send(JSON.stringify(batchMessage));
    // setStatus("Файлы отправлены. Ожидание обработки...");
  }, []);

  const uploadFilesChunked = useCallback(async (files) => {
    // setStatus(`Начинаем chunked загрузку ${files.length} файлов`);

    let totalChunks = 0;
    let sentChunks = 0;

    Array.from(files).forEach((file) => {
      totalChunks += Math.ceil(file.size / CHUNK_SIZE);
    });

    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      const file = files[fileIndex];
      const chunks = Math.ceil(file.size / CHUNK_SIZE);

      // setStatus(`Загрузка файла: ${file.name} (${fileIndex + 1}/${files.length})`);

      for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const base64Chunk = await fileToBase64(chunk);

        const chunkMessage = {
          type: "file_chunk",
          fileData: {
            fileName: file.name,
            contentType: file.type,
            chunkIndex: chunkIndex,
            totalChunks: chunks,            
            totalFilesChunks: totalChunks,
            data: base64Chunk.split(",")[1],
            fileSize: file.size,
          },
        };

        wsRef.current.send(JSON.stringify(chunkMessage));

        sentChunks++;        

        await new Promise((resolve) => setTimeout(resolve, 10));
      }
    }
    
    wsRef.current.send(JSON.stringify({ type: "process" }));
  }, []);

  const processFiles = useCallback(() => {
    console.log("processFiles called - will be handled automatically");
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.isManualDisconnect = true;

      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    setIsConnecting(false);
    setIsConnected(false);
    setProgress(0);
    setStatus("");
  }, []);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const reset = useCallback(() => {
    setIsConnecting(false);
    setIsConnected(false);
    setProgress(0);
    setStatus("");
    setErrorWithRef("");
    setSuccess(false);
    setResults(null);
    uploadedFilesRef.current = [];

    if (wsRef.current) {
      wsRef.current.close(1000, "Reset");
      wsRef.current = null;
    }
  }, []);

  return {
    isConnecting,
    isConnected,
    progress,
    status,
    error,
    success,
    results,
    connect,
    uploadFiles,
    processFiles,
    disconnect,
    reset,
  };
};
